import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Select,
  Popover,
  Checkbox,
  Button,
} from '@shopify/polaris';

const EditStoryModal = ({ 
  isOpen, 
  onClose, 
  selectedStory,
  setToastMessage, 
  setToastActive,
  onStoryUpdated 
}) => {
  const [editAgePopoverActive, setEditAgePopoverActive] = useState(false);
  
  const ageOptions = ['0-2', '2-4', '4-6', '6-8', '8-10'];

  const [editStoryData, setEditStoryData] = useState({
    slug: '',
    title: '',
    description: '',
    gender: 'boy',
    age: [],
    language: 'english'
  });

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Initialize form data when selectedStory changes
  useEffect(() => {
    if (selectedStory && isOpen) {
      setEditStoryData({
        slug: selectedStory.slug,
        title: selectedStory.title,
        description: selectedStory.description,
        gender: selectedStory.gender,
        age: selectedStory.age ? selectedStory.age.split(',').map(a => a.trim()) : [],
        language: selectedStory.language
      });
    }
  }, [selectedStory, isOpen]);

  // Reset popover when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditAgePopoverActive(false);
    }
  }, [isOpen]);

  const handleUpdateStory = async () => {
    if (!editStoryData.title || !editStoryData.slug) {
      setToastMessage('Please fill in all required fields');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }

    if (!selectedStory) return;

    try {
      // Import the service here to avoid circular dependencies
      const { storiesService } = await import('../../services/storiesService');
      
      await storiesService.updateStory(selectedStory._id, {
        ...editStoryData,
        age: editStoryData.age.join(', ')
      });
      
      setToastMessage('Story Updated Successfully');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      
      onClose();
      
      // Notify parent component to refresh stories list
      if (onStoryUpdated) {
        onStoryUpdated();
      }
    } catch (err) {
      console.error('Error updating story:', err);
      setToastMessage(`Error: ${err.message || 'Failed to update story'}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    }
  };

  if (!selectedStory) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Edit Story'}</div>}
      primaryAction={{
        content: 'Save',
        onAction: handleUpdateStory,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        <TextField
          label="Title"
          value={editStoryData.title}
          onChange={(value) => setEditStoryData(prev => ({ 
            ...prev, 
            title: value,
            slug: generateSlug(value) // Auto-generate slug from title
          }))}
        />
        <TextField
          label="Slug"
          value={editStoryData.slug}
          onChange={(value) => setEditStoryData(prev => ({ ...prev, slug: value }))}
          readOnly
        />
        <TextField
          label="Description"
          value={editStoryData.description}
          onChange={(value) => setEditStoryData(prev => ({ ...prev, description: value }))}
          multiline={2}
          autoComplete="off"
        />
        <Select
          label="Gender"
          options={[
            { label: 'Boy', value: 'boy' },
            { label: 'Girl', value: 'girl' },
            { label: 'Both', value: 'both' },
          ]}
          onChange={(value) => setEditStoryData(prev => ({ ...prev, gender: value }))}
          value={editStoryData.gender}
        />
        {/* Age Selection */}
        <div>
          <Text variant="bodyMd" as="label" style={{ display: 'block', marginBottom: 4, textAlign: 'left' }}>Age</Text>
          <Popover
            active={editAgePopoverActive}
            activator={
              <Button disclosure fullWidth onClick={() => setEditAgePopoverActive((a) => !a)} style={{ 
                textAlign: 'left', 
                justifyContent: 'flex-start',
                paddingLeft: '12px',
                paddingRight: '12px'
              }}>
                <div style={{ 
                  width: '100%', 
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  {editStoryData.age.length > 0 ? editStoryData.age.join(', ') : 'Select Age'}
                </div>
              </Button>
            }
            onClose={() => setEditAgePopoverActive(false)}
            fullWidth
          >
            <BlockStack gap="200" style={{ padding: 12, minWidth: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
              {ageOptions.map(range => (
                <Checkbox
                  key={range}
                  label={range}
                  checked={editStoryData.age.includes(range)}
                  onChange={(checked) => {
                    setEditStoryData(prev => ({
                      ...prev,
                      age: checked
                        ? [...prev.age, range]
                        : prev.age.filter(a => a !== range)
                    }));
                  }}
                />
              ))}
            </BlockStack>
          </Popover>
        </div>
        <Select
          label="Language"
          options={[
            { label: 'Arabic (Egyptian)', value: 'arabic-egyptian' },
            { label: 'Arabic (Saudi Arabia)', value: 'arabic-saudi' },
            { label: 'Arabic (UAE)', value: 'arabic-uae' },
            { label: 'Arabic (Qatar)', value: 'arabic-qatar' },
            { label: 'Arabic (Kuwait)', value: 'arabic-kuwait' },
            { label: 'English', value: 'english' },
            { label: 'Spanish', value: 'spanish' },
            { label: 'German', value: 'german' },
            { label: 'French', value: 'french' },
            { label: 'Italian', value: 'italian' },
          ]}
          onChange={(value) => setEditStoryData(prev => ({ ...prev, language: value }))}
          value={editStoryData.language}
        />
      </BlockStack>
    </Modal>
  );
};

export default EditStoryModal; 